import { Link } from 'react-router-dom';
import { LayoutGrid, PanelTop } from 'lucide-react';
import { useChrome } from '../context/ChromeContext';
import { WORKTOP_PATH } from '@/lib/arche-url';
import { useGlobalFiasAIOptional } from '@/components/fias-ai/hooks/GlobalFiasAIContext';

interface FocusModeRestoreControlsProps {
  /** Canonical arche id (`arche_*`) whose chrome state this restores. */
  archeId: string;
  /** Optional wrapper classes (e.g. `p-2 border-b`) for the host layout. */
  className?: string;
}

/**
 * The restore affordance for an arche's focus mode ("maximized" — upper chrome
 * hidden via ArcheHeader's Hide-headers/maximize button).
 *
 * Once chrome is hidden, both `ArcheHeader` and the platform `Header` unmount, so
 * without this control there is NO in-app way back — the arche is stuck maximized
 * until a reload. Any arche that exposes the maximize button MUST render this
 * somewhere in its content area so a maximize is always reversible. (Email hit
 * exactly this gap first; the pattern is now shared.)
 *
 * Renders nothing unless THIS arche's chrome is hidden, so it is inert in the
 * normal (non-maximized) state — including the `className` wrapper, which only
 * appears while restoring. Four icon controls, mirroring `ExpandedArcheHeader`
 * so the two focus-mode bars offer the same destinations: a Fias-home link, a
 * Worktop link (the platform Header that normally carries it is hidden here), an
 * Open-Fias-AI button (only when the app-wide assistant provider is mounted),
 * and a Show-headers (un-maximize) button.
 */
export function FocusModeRestoreControls({ archeId, className }: FocusModeRestoreControlsProps) {
  const { isHeadersHidden, showHeaders } = useChrome();
  const ai = useGlobalFiasAIOptional();
  if (!isHeadersHidden(archeId)) return null;

  return (
    <div className={`flex items-center gap-1 ${className ?? ''}`}>
      <Link
        to="/"
        className="rounded-md border border-border bg-card/90 p-1.5 shadow-sm hover:opacity-80"
        title="Back to Fias home"
        aria-label="Back to Fias home"
      >
        <img src="/fias-logo.png" alt="Fias" className="h-4 w-auto dark:invert" />
      </Link>
      <Link
        to={WORKTOP_PATH}
        className="rounded-md border border-border bg-card/90 p-1.5 text-muted-foreground shadow-sm hover:text-primary"
        title="Go to Worktop"
        aria-label="Go to Worktop"
      >
        <LayoutGrid className="h-4 w-4" />
      </Link>
      {ai && (
        <button
          type="button"
          onClick={() => ai.openPanel()}
          className="rounded-md border border-border bg-card/90 p-1.5 text-muted-foreground shadow-sm hover:text-primary"
          title="Open Fias AI"
          aria-label="Open Fias AI"
        >
          <img src="/Heptagen_Logo_1.png" alt="" className="h-4 w-4" />
        </button>
      )}
      <button
        type="button"
        onClick={() => showHeaders(archeId)}
        className="rounded-md border border-border bg-card/90 p-1.5 text-muted-foreground shadow-sm hover:text-primary"
        title="Show headers"
        aria-label="Show headers"
      >
        <PanelTop className="h-4 w-4" />
      </button>
    </div>
  );
}

export default FocusModeRestoreControls;
